import { fetchAuthSession } from 'aws-amplify/auth'

let pendingSession
let isSigningOut = false

export async function getCurrentAuthSession() {
  // A lookup started now could refresh and store tokens after signOut has already
  // cleared them, which is the same write-back race runSignOut exists to close.
  // Reporting no tokens is also just true: the user is on their way out.
  if (isSigningOut) {
    return {}
  }

  if (!pendingSession) {
    pendingSession = fetchAuthSession()
  }

  const current = pendingSession

  try {
    return await current
  } finally {
    // Every caller sharing this lookup runs the finally, so each one would clear
    // whatever is stored rather than the lookup it actually awaited. Today they all
    // drain in the same microtask batch, before any new call can replace it, so
    // clearing blindly happens to be safe. That is an accident of there being a
    // single await here: add one in front of this and a late caller starts dropping
    // a newer in-flight lookup, costing an extra fetchAuthSession per request.
    // Comparing identity states the invariant instead of relying on the timing.
    if (pendingSession === current) {
      pendingSession = null
    }
  }
}

export async function getCurrentAccessToken() {
  const { tokens } = await getCurrentAuthSession()

  return tokens?.accessToken?.toString() || null
}

// Amplify refreshes tokens and stores them unconditionally: TokenOrchestrator
// .refreshTokens() awaits the network call, then calls setTokens() with no check
// for a sign-out that happened while it was in flight. signOut() revokes the
// refresh token before clearing the store, so a refresh that reaches Cognito
// after revocation fails and clears the tokens itself, but one that gets in just
// before it succeeds and writes fresh tokens back — after clearTokens() has
// already run. The next reload then restores the session the user asked to end.
//
// Now that every protected request can start a refresh, a logout clicked while a
// page is loading data is enough to hit that. Draining first orders the two: the
// refresh stores its tokens, and only then does signOut revoke and clear them.
// The flag covers the rest of the window, since signOut awaits a revoke round
// trip during which another request would otherwise start a fresh lookup.
export async function runSignOut(performSignOut) {
  isSigningOut = true

  try {
    if (pendingSession) {
      // Whatever this settles to is signOut's problem now, not ours.
      await pendingSession.catch(() => {})
    }

    return await performSignOut()
  } finally {
    isSigningOut = false
  }
}
